import Modal from '../../modals/modal';
import QuizTooltip from './quiz-tooltip';

const settings = {
    // This just automatically opens the modal on page load if set to "true"
    testMode: false,

    // If "true", we present the modal via dialog.show() instead of dialog.showModal()...
    // this is necessary for the Google autocomplete elements to be visible on
    // top of our <dialog> element; the downside is that we won't be a true modal
    // and focus won't be trapped
    useAutocomplete: true,

    // The initial screen to navigate to
    initialScreen: 'intro',
};

let template = null;

export default class Quiz extends Modal {
    constructor(callbacks = {}) {
        // Load our template if we haven't yet
        if (!template) {
            // Locate our template element and remove it from the DOM
            template = document.querySelector('#surgeon-locator-quiz');
            if (!template) {
                throw new Error();
            }

            template.remove();
            template = template.outerHTML;
        }

        // Call base constructor
        super(template);

        this.callbacks = callbacks;

        this.visible = false;
        this.currentScreenId = null;
        this.userResponses = {};

        // Find our DOM elements
        this.radiosAndCheckboxes = this.container.querySelectorAll(
            'input[type="radio"], input[type="checkbox"]',
        );
        this.textInputs = this.container.querySelectorAll('input[type="text"]');
        this.initiallyDisabledButtons = this.container.querySelectorAll(
            '.surgeon-locator-quiz__continue-button:disabled',
        );

        // Setup screens
        this.screens = {};
        const screenElements = this.container.querySelectorAll('[data-quiz-screen]');
        for (const screenElement of screenElements) {
            const screenId = screenElement.getAttribute('data-quiz-screen');
            if (!screenId) {
                continue;
            }

            this.screens[screenId] = screenElement;
            setupScreen.call(this, screenElement, screenId);
        }

        // If "testMode" is set to true, open immediately
        if (settings.testMode) {
            this.show();
            this.navigate('mastectomy-yes-4');
        }
    }

    show() {
        if (this.visible) {
            return;
        }

        // Reset screens and recorded responses
        this.userResponses = {};

        for (const element of this.radiosAndCheckboxes) {
            element.checked = false;
        }

        for (const element of this.textInputs) {
            element.value = '';
        }

        for (const element of this.initiallyDisabledButtons) {
            element.disabled = true;
        }

        // Navigate to the first screen
        this.navigate(settings.initialScreen);

        // Call base class
        if (settings.useAutocomplete) {
            super.show(false, false);
        } else {
            super.show();
        }
    }

    navigate(toScreenId) {
        if (toScreenId === this.currentScreenId) {
            return;
        }

        if (!Object.hasOwn(this.screens, toScreenId)) {
            return;
        }

        const toScreen = this.screens[toScreenId];
        toScreen.classList.add('current-screen');

        if (this.currentScreenId) {
            const fromScreen = this.screens[this.currentScreenId];
            fromScreen.classList.remove('current-screen');
        }

        // Update current screen ID
        this.currentScreenId = toScreenId;
    }
}

/*
    Internal functions
*/

/**
 * @this Quiz
 * @param {*} screenElement
 * @param {*} screenId
 */
function setupScreen(screenElement, screenId) {
    // Get references to our form and continue button
    const form = screenElement.querySelector('form');
    if (!form) {
        throw new Error();
    }

    const continueButton = form.querySelector(
        '.surgeon-locator-quiz__continue-button',
    );
    if (!continueButton) {
        throw new Error();
    }

    // Setup simple continue buttons that aren't conditional
    if (
        !continueButton.disabled &&
        continueButton.hasAttribute('data-next-quiz-screen')
    ) {
        // This screen doesn't have any conditional logic - simply
        // navigate on submit
        const toScreenId = continueButton.getAttribute('data-next-quiz-screen');
        form.addEventListener('submit', (event) => {
            event.preventDefault();
            this.navigate(toScreenId);
        });
    }

    // Setup tooltips
    QuizTooltip.init(screenElement);

    // Setup screen-specific logic
    switch (screenId) {
        case 'mastectomy':
            // Initial yes or no question
            {
                let selectedValue;
                form.addEventListener('submit', (event) => {
                    event.preventDefault();

                    if (selectedValue == 'yes') {
                        this.userResponses.type = 'plastic-surgeon';
                        this.navigate('mastectomy-yes-1');
                    } else if (selectedValue == 'no') {
                        this.userResponses.type = 'both';
                        this.navigate('mastectomy-no-1');
                    }
                });

                const radios = form.querySelectorAll('input');
                for (const radio of radios) {
                    radio.addEventListener('change', () => {
                        for (const testRadio of radios) {
                            if (testRadio.checked) {
                                selectedValue = testRadio.value;
                                continueButton.disabled = false;
                            }
                        }
                    });
                }
            }
            break;

        case 'mastectomy-no-2':
            // Record selected reconstruction types
            {
                form.addEventListener('submit', (event) => {
                    event.preventDefault();

                    const radios = form.querySelectorAll('input');
                    for (const radio of radios) {
                        switch (radio.value) {
                            case 'ibr':
                                this.userResponses.ibr = radio.checked;
                                break;

                            case 'afbr':
                                this.userResponses.afbr = radio.checked;
                                break;

                            case 'afc':
                                this.userResponses.afc = radio.checked;
                                break;
                        }
                    }

                    this.navigate('mastectomy-no-3');
                });
            }
            break;

        case 'mastectomy-no-3':
            // City or state required
            {
                let trimmedValue;
                const textInput = form.querySelector('input');

                const changeCallback = () => {
                    trimmedValue = textInput.value.trim();
                    if (trimmedValue.length > 0) {
                        continueButton.disabled = false;
                    } else {
                        continueButton.disabled = true;
                    }
                };

                textInput.addEventListener('input', changeCallback);
                textInput.addEventListener('change', changeCallback);

                form.addEventListener('submit', (event) => {
                    event.preventDefault();

                    // Run the change callback again - Autocomplete
                    // doesn't trigger input or change events sometimes
                    // and I'm not sure what the quick fix would be
                    changeCallback();
                    if (continueButton.disabled) {
                        return;
                    }

                    // We're finished at this point
                    this.userResponses.query = trimmedValue;

                    // Perform callback
                    if (this.callbacks.searchCallback) {
                        this.callbacks.searchCallback(this.userResponses);
                    }

                    // Close the Quiz
                    this.close();
                });

                // Setup autocomplete on query field
                setupAutocomplete.call(this, textInput);
            }
            break;

        case 'mastectomy-yes-2':
            // Radio selection required, and next screen is based on
            // whether users select the "autologous" radio button
            {
                let selectedValue;
                form.addEventListener('submit', (event) => {
                    event.preventDefault();

                    if (selectedValue == 'autologous') {
                        this.navigate('unqualified');
                    } else {
                        this.navigate('mastectomy-yes-3');
                    }
                });

                const radios = form.querySelectorAll('input');
                for (const radio of radios) {
                    radio.addEventListener('change', () => {
                        for (const testRadio of radios) {
                            if (testRadio.checked) {
                                selectedValue = testRadio.value;
                                continueButton.disabled = false;
                            }
                        }
                    });
                }
            }
            break;

        case 'mastectomy-yes-3':
            // Record selected reconstruction types
            {
                form.addEventListener('submit', (event) => {
                    event.preventDefault();

                    const checkboxes = form.querySelectorAll('input');
                    for (const checkbox of checkboxes) {
                        switch (checkbox.value) {
                            case 'ibr':
                                this.userResponses.ibr = checkbox.checked;
                                break;

                            case 'afbr':
                                this.userResponses.afbr = checkbox.checked;
                                break;

                            case 'afc':
                                this.userResponses.afc = checkbox.checked;
                                break;
                        }
                    }

                    this.navigate('mastectomy-yes-4');
                });
            }
            break;

        case 'mastectomy-yes-4':
            // City or state required
            {
                let trimmedValue;
                const textInput = form.querySelector('input');

                const changeCallback = () => {
                    trimmedValue = textInput.value.trim();
                    if (trimmedValue.length > 0) {
                        continueButton.disabled = false;
                    } else {
                        continueButton.disabled = true;
                    }
                };

                textInput.addEventListener('input', changeCallback);
                textInput.addEventListener('change', changeCallback);

                form.addEventListener('submit', (event) => {
                    event.preventDefault();

                    // Run the change callback again - Autocomplete
                    // doesn't trigger input or change events sometimes
                    // and I'm not sure what the quick fix would be
                    changeCallback();
                    if (continueButton.disabled) {
                        return;
                    }

                    // We're finished at this point
                    this.userResponses.query = trimmedValue;

                    // Perform callback
                    if (this.callbacks.searchCallback) {
                        this.callbacks.searchCallback(this.userResponses);
                    }

                    // Close the Quiz
                    this.close();
                });

                // Setup autocomplete on query field
                setupAutocomplete.call(this, textInput);
            }
            break;

        case 'unqualified':
            // Button on this screen simply closes the quiz
            {
                form.addEventListener('submit', (event) => {
                    event.preventDefault();

                    // Close the Quiz
                    this.close();
                });
            }
            break;
    }
}

function setupAutocomplete(textInput) {
    if (!settings.useAutocomplete) {
        return;
    }

    // Initialize Autocomplete for this field
    new google.maps.places.Autocomplete(textInput, {
        componentRestrictions: {country: 'us'},
        fields: ['address_components', 'geometry'],
    });
}
